/** Bulk Set schedule on outline root tasks — dialog once, patch each task, in-place row update. */
import { ScheduleRule, ScheduleWeekday } from '@jms/model/scheduling/schedule.rule';
import { EntityDataService } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { SemanticCommand } from '@workbuddy/ui/services/keyboard.shortcuts';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { BulkCommandArgs, BulkCommandOutcome } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { openScheduleEditor } from '@workbuddy/ui/v2/fields/schedule';
import { OutlineEntityKinds, OutlineRow } from '../outline.authoring.model';
import { isOutlineRootTask } from '../outline.selection';

export const OUTLINE_SET_SCHEDULE_ACTION = SemanticCommand.SetSchedule;

/**
 * What the schedule editor closes with. The dialog has grown from returning a bare
 * `ScheduleRule` to returning the model alongside the two task-level anchors the editor
 * now owns, and both shapes are accepted here so this flow does not hinge on which version is loaded.
 */
export type OutlineScheduleEditorResult = ScheduleRule | { schedule: ScheduleRule; startsOn?: string; lastCompleted?: string };

/** A type alias, not an interface: the row patcher takes a `Record<string, unknown>`, which only an alias satisfies. */
export type OutlineTaskSchedulePatch = {
  schedule: ScheduleRule;
  /** Sent as null to clear, which is how the editor says "start with the Service Line" again. */
  startsOn?: string | null;
  lastCompleted?: string | null;
};

/**
 * Bulk applies the editor's COMPLETE answer — cadence plus anchors. The dialog shows "First due"
 * and "Last serviced" as part of the schedule being set, so what lands on every selected task is
 * exactly what was on screen, including "With the Service Line" clearing a stale per-task override
 * a replaced schedule would otherwise drag along.
 *
 * The anchors are only touched when the result actually carries them. Under the older bare-model
 * contract they are absent from the payload entirely rather than sent as clears, so a task's stored
 * `startsOn` survives an editor that had no way to ask about it.
 */
export function outlineSchedulePatch(result: OutlineScheduleEditorResult): OutlineTaskSchedulePatch {

  if (!('schedule' in result)) return { schedule: result };
  return {
    schedule: result.schedule,
    ...('startsOn' in result ? { startsOn: result.startsOn ?? null } : {}),
    ...('lastCompleted' in result ? { lastCompleted: result.lastCompleted ?? null } : {}),
  };
}

export interface OutlineRowPatcher {
  patchDisplayedRow(id: string, data: Record<string, unknown>): void;
  refreshServerSideRoute?(route: string[]): void;
  setFocusedItemId?(id: string | null): void;
  $gridApi?(): { refreshServerSide?(args: { purge: boolean }): void } | null;
}

export interface OutlineSetScheduleDeps {
  /** The entity pair this run is acting on, so one handler serves the live outline and the template one. */
  kinds: OutlineEntityKinds;
  dialogs: WorkBuddyDialogService;
  entityData: EntityDataService;
  store: OutlineRowPatcher;
  toasts: ToastService;
  today: string;
  workWeek: ScheduleWeekday[];
  timezone?: string;
}

export async function handleOutlineSetSchedule(args: BulkCommandArgs<OutlineRow>, deps: OutlineSetScheduleDeps): Promise<BulkCommandOutcome> {

  if ((args.bulkActionRequest?.actionId ?? args.action) !== OUTLINE_SET_SCHEDULE_ACTION) return { handled: false };
  const tasks = (args.data ?? []).filter(row => isOutlineRootTask(row, deps.kinds));
  if (!tasks.length) return { handled: true, clearSelection: false };

  const result: OutlineScheduleEditorResult | undefined = await openScheduleEditor(deps.dialogs, {
    context: {
      taskName: tasks.length === 1 ? tasks[0].name : `${tasks.length} tasks`,
      effectiveFrom: deps.today,
      timezone: deps.timezone ?? 'UTC',
      today: deps.today,
      workWeek: deps.workWeek,
      holidays: [],
      // The one place this bulk flow differs between the two outlines. `datesVisits: false` is the
      // template outline saying it sits on no calendar, which is exactly what the editor's template
      // mode means — so the flag is translated here rather than restated as a second setting.
      mode: deps.kinds.datesVisits === false ? 'template' : 'live',
    },
  });
  if (result === undefined) return { handled: true, clearSelection: false };

  const patch = outlineSchedulePatch(result);
  const patcher = deps.entityData.getPatch(deps.kinds.task);
  let updated = 0;
  let failed = 0;
  for (const task of tasks) {
    try {
      await patcher.mutateAsync({ id: task.sourceId, data: patch });
      deps.store.patchDisplayedRow(`${deps.kinds.taskPrefix}${task.sourceId}`, patch);
      updated += 1;
    } catch {
      failed += 1;
    }
  }

  if (failed && !updated) deps.toasts.error('Set schedule', 'Action failed, please try again.');
  else if (failed) deps.toasts.warning('Set schedule', `Updated ${updated} of ${tasks.length} tasks.`);
  else deps.toasts.success('Set schedule', `Updated ${updated} task${updated === 1 ? '' : 's'}.`);
  return { handled: true };
}
